// Loads a tier's glb (contract section 2) and prepares it for cheap rendering:
//  - KTX2 (Basis) textures + meshopt geometry
//  - static batching: meshes sharing a material are merged into one draw call (world space)
//  - matrices frozen (matrixAutoUpdate off)
//  - anisotropic filtering on baked textures
//  - reflective surfaces (extras.reflect) patched with the reflection layer (opts.reflect), else they become
//    plain unlit materials and are merged with the other surfaces that use the same texture
//  - normals dropped from every mesh that does not reflect (unlit shading never reads them)
//  - meshopt decoding in workers (no long main-thread task during parse)
//  - screen nodes kept separate (their material is swapped for video)
//  - interactive nodes (extras.interactive, contract section 7) and tintable nodes (extras.tint_id) kept
//    separate; tintable nodes get their own material clone. Until the pipeline exports those extras, the
//    round screen (nodes round_screen_*) is `room_controls` and mid_panels_perf (the merged white centre
//    panel of the monitor variant) is `centre_panel`.

import {
  type Camera,
  BufferAttribute,
  BufferGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  type CompressedTexture,
  type Material,
  type Object3D,
  type Texture,
  type WebGLRenderer,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { makeReflective, readReflection, type ReflectionSettings } from '../render/reflections';
import { estimateTextureBytes } from '../render/memory';
import type { Manifest, TierName } from './manifest';

export interface LoadedScene {
  root: Group;
  reflective: MeshBasicMaterial[];
  screens: Map<string, Mesh>;
  /** interactive id -> meshes (frozen, world transform), raycast targets */
  interactive: Map<string, Mesh[]>;
  /** tint id -> meshes (each with its own material) */
  tintables: Map<string, Mesh[]>;
  /** glb node name -> its meshes as world-space position-only copies, not in the scene (opts.shapes: the ladders that
   *  floor portals outline; the drawn meshes may be batched with others) */
  shapes: Map<string, Mesh[]>;
  /** Every MeshBasicMaterial of the room except screen materials (the room shading patches these). */
  roomMaterials: MeshBasicMaterial[];
  textures: Set<Texture>;
  textureBytes: number;
  drawables: number;
  triangles: number;
  /** ms: fetch, parse (glTF + meshopt + KTX2 transcode), prepare (batching, materials) */
  timings: { fetch: number; parse: number; prepare: number; bytes: number };
  /** Called before every render of the room scene, before frustum culling (outdoor field, 2026-11-01: one-off meshes
   *  moved to their copy nearest the camera). Missing in baked rooms. */
  beforeRender?: (camera: Camera) => void;
}

let ktx2: KTX2Loader | null = null;

export async function loadScene(
  renderer: WebGLRenderer,
  manifest: Manifest,
  base: string,
  tier: TierName,
  onProgress: (fraction: number) => void,
  opts: { reflect: boolean; background?: boolean; shapes?: ReadonlySet<string> } = { reflect: true },
): Promise<LoadedScene> {
  const info = manifest.tiers[tier];
  if (!info) throw new Error(`tier ${tier} missing from manifest`);
  if (manifest.outdoor) {
    // an outdoor field (2026-11-01): generated here from the manifest's setting, no glb (outdoor/field-scene.ts)
    const { buildFieldScene } = await import('../outdoor/field-scene');
    onProgress(1);
    return buildFieldScene(manifest.outdoor, tier);
  }
  const loader = new GLTFLoader().setKTX2Loader(initDecoders(renderer)).setMeshoptDecoder(MeshoptDecoder);
  const url = base + info.url;
  const t0 = performance.now();
  const buffer = await fetchWithProgress(url, info.bytes || 0, onProgress);
  const t1 = performance.now();
  performance.mark(`lr3d:${tier}:fetched`);
  const resourcePath = url.slice(0, url.lastIndexOf('/') + 1);
  const gltf = await loader.parseAsync(buffer, resourcePath);
  const t2 = performance.now();
  performance.mark(`lr3d:${tier}:parsed`);
  onProgress(1);

  const root = new Group();
  root.name = 'scene_root';
  const src = gltf.scene;
  src.updateMatrixWorld(true);

  const screenNodes = new Set((manifest.screens ?? []).map((s) => s.node));
  let anyInteractive = false, anyTint = false;
  src.traverse((o) => {
    if (o.userData.interactive !== undefined) anyInteractive = true;
    if (o.userData.tint_id !== undefined) anyTint = true;
  });
  const nodeName = (m: Object3D) => (m.parent && m.parent !== src && !(m.parent as Mesh).isMesh && m.parent.name ? `${m.parent.name}/${m.name}` : m.name);
  const interactiveOf = (m: Mesh): string | null => {
    const v = m.userData.interactive;
    if (typeof v === 'string' && v) return v;
    if (!anyInteractive && /(^|\/)round_screen_/.test(nodeName(m))) return 'room_controls';
    return null;
  };
  const tintOf = (m: Mesh): string | null => {
    const v = m.userData.tint_id;
    if (typeof v === 'string' && v) return v;
    if (!anyTint && (m.name === 'mid_panels_perf' || m.parent?.name === 'mid_panels_perf')) return 'centre_panel';
    return null;
  };
  const keep: Mesh[] = [];
  const groups = new Map<string, Mesh[]>();
  const maxAniso = renderer.capabilities.getMaxAnisotropy();
  const aniso = Math.min(maxAniso, tier === 'mobile' ? 4 : 8);
  const textures = new Set<Texture>();
  const reflectionOf = (m: Mesh) => (opts.reflect ? readReflection(m) : null);
  const shapes = new Map<string, Mesh[]>();

  src.traverse((o: Object3D) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh) return;
    // glTF node extras land on the node; for multi-primitive meshes the node is the parent Group
    const pu = mesh.parent?.userData;
    if (pu) {
      if (mesh.userData.reflect === undefined && pu.reflect !== undefined) Object.assign(mesh.userData, pu);
      if (mesh.userData.interactive === undefined && pu.interactive !== undefined) mesh.userData.interactive = pu.interactive;
      if (mesh.userData.tint_id === undefined && pu.tint_id !== undefined) mesh.userData.tint_id = pu.tint_id;
    }
    const shapeName = opts.shapes?.has(mesh.name) ? mesh.name : mesh.parent && opts.shapes?.has(mesh.parent.name) ? mesh.parent.name : null;
    if (shapeName) {
      const copy = new Mesh(worldPositions(mesh));
      copy.name = `shape:${shapeName}`;
      copy.matrixAutoUpdate = false;
      copy.updateMatrixWorld(true);
      let l = shapes.get(shapeName);
      if (!l) shapes.set(shapeName, (l = []));
      l.push(copy);
    }
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) collectTextures(m, textures);
    const iid = interactiveOf(mesh);
    mesh.userData.interactive = iid ?? undefined;
    const tid = tintOf(mesh);
    mesh.userData.tint_id = tid ?? undefined;
    if (tid) {
      // own material: the tint must not reach other nodes that share the glTF material
      if (!Array.isArray(mesh.material)) {
        const old = mesh.material;
        mesh.material = old.clone();
        mesh.material.userData = { ...old.userData };
      }
    }
    const screenName = screenNodes.has(mesh.name) ? mesh.name : mesh.parent && screenNodes.has(mesh.parent.name) ? mesh.parent.name : null;
    if (screenName) {
      mesh.userData.screenNode = screenName;
      keep.push(mesh);
      return;
    }
    if (iid || tid) {
      keep.push(mesh);
      return;
    }
    if (Array.isArray(mesh.material) || (mesh as Mesh & { isSkinnedMesh?: boolean }).isSkinnedMesh || mesh.morphTargetInfluences) {
      keep.push(mesh);
      return;
    }
    const key = materialKey(mesh, reflectionOf(mesh));
    let list = groups.get(key);
    if (!list) groups.set(key, (list = []));
    list.push(mesh);
  });

  for (const t of textures) {
    t.anisotropy = aniso;
    t.needsUpdate = true;
  }

  // ---- static batching (in the background, yield between groups so walking does not stutter)
  for (const list of groups.values()) {
    if (opts.background) await new Promise((r) => setTimeout(r, 0));
    if (list.length === 1) {
      keep.push(list[0]!);
      continue;
    }
    const geos: BufferGeometry[] = [];
    const dropNormal = !reflectionOf(list[0]!);
    for (const m of list) geos.push(toWorldFloat(m, dropNormal));
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) {
      keep.push(...list);
      continue;
    }
    merged.computeBoundingSphere();
    merged.computeBoundingBox();
    const first = list[0]!;
    const mesh = new Mesh(merged, first.material);
    mesh.name = `batch:${list.map((m) => m.name).slice(0, 4).join(',')}${list.length > 4 ? `,+${list.length - 4}` : ''}`;
    mesh.userData = { ...first.userData, merged: list.map((m) => m.name) };
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrixWorld(true);
    root.add(mesh);
    for (const m of list) m.geometry.dispose();
  }
  for (const m of keep) {
    if (!m.userData.screenNode && !reflectionOf(m) && m.geometry.attributes.normal) m.geometry.deleteAttribute('normal');
    // re-parent at its world transform, frozen
    m.matrixWorld.decompose(m.position, m.quaternion, m.scale);
    m.removeFromParent();
    root.add(m);
    m.updateMatrix();
    m.matrixAutoUpdate = false;
  }
  root.updateMatrixWorld(true);
  root.matrixAutoUpdate = false;

  // ---- materials: reflection layer, screens list
  const reflective = new Set<MeshBasicMaterial>();
  const screens = new Map<string, Mesh>();
  const interactive = new Map<string, Mesh[]>();
  const tintables = new Map<string, Mesh[]>();
  const roomMaterials = new Set<MeshBasicMaterial>();
  const push = (map: Map<string, Mesh[]>, k: string, m: Mesh) => { let l = map.get(k); if (!l) map.set(k, (l = [])); l.push(m); };
  let triangles = 0;
  let drawables = 0;
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh) return;
    drawables++;
    const g = mesh.geometry;
    triangles += (g.index ? g.index.count : g.attributes.position!.count) / 3;
    if (mesh.userData.screenNode) screens.set(mesh.userData.screenNode as string, mesh);
    if (mesh.userData.interactive) push(interactive, mesh.userData.interactive as string, mesh);
    if (mesh.userData.tint_id) push(tintables, mesh.userData.tint_id as string, mesh);
    const r = reflectionOf(mesh);
    const mat = mesh.material as Material;
    if (!mesh.userData.screenNode && (mat as MeshBasicMaterial).isMeshBasicMaterial) roomMaterials.add(mat as MeshBasicMaterial);
    if (r && (mat as MeshBasicMaterial).isMeshBasicMaterial) {
      if (!g.attributes.normal) g.computeVertexNormals();
      makeReflective(mat as MeshBasicMaterial, r);
      reflective.add(mat as MeshBasicMaterial);
    }
  });

  let textureBytes = 0;
  for (const t of textures) textureBytes += estimateTextureBytes(t);

  const t3 = performance.now();
  // screens never show their baked texture (the cad placeholder art): each screen mesh starts on plain black, so
  // whatever restores a screen's "original" material (posters, round screen, a tier or room switch) restores black
  // (a view screen, content "view": the gallery's window view, keeps its baked image until the room's art replaces it)
  const viewNodes = new Set((manifest.screens ?? []).filter((s) => s.content === 'view' || s.node === 'window_view').map((s) => s.node));
  for (const [node, m] of screens) if (!viewNodes.has(node)) m.material = new MeshBasicMaterial({ color: 0x000000, name: `screen_black:${m.name}` });
  return { root, reflective: [...reflective], screens, interactive, tintables, shapes, roomMaterials: [...roomMaterials], textures, textureBytes, drawables, triangles, timings: { fetch: t1 - t0, parse: t2 - t1, prepare: t3 - t2, bytes: buffer.byteLength } };
}

/**
 * The shared KTX2 loader and the meshopt decoder workers, set up once, before the first glb fetch: `init()`
 * starts the Basis transcoder (wasm) download now, in parallel with the glb, instead of when the parser meets
 * the first KTX2 texture.
 */
function initDecoders(renderer: WebGLRenderer): KTX2Loader {
  if (!ktx2) {
    ktx2 = new KTX2Loader().detectSupport(renderer); // three's own transcoder (a hashed asset in the build)
    const cores = navigator.hardwareConcurrency || 4;
    ktx2.setWorkerLimit(Math.max(1, Math.min(4, cores - 1)));
    MeshoptDecoder.useWorkers?.(Math.max(1, Math.min(4, Math.floor(cores / 2))));
    void ktx2.init();
  }
  return ktx2;
}

/** fetch() into an ArrayBuffer, reporting progress (total from Content-Length, else the manifest's byte count). */
async function fetchWithProgress(url: string, expected: number, onProgress: (f: number) => void): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const total = Number(res.headers.get('content-length')) || expected;
  if (!res.body || !total) return res.arrayBuffer();
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let n = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    n += value.length;
    onProgress(Math.min(1, n / total));
  }
  const out = new Uint8Array(n);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.length; }
  return out.buffer;
}
function collectTextures(m: Material, out: Set<Texture>): void {
  for (const v of Object.values(m)) if (v && (v as Texture).isTexture) out.add(v as Texture);
}

/**
 * Batching key. Reflective meshes batch per material and reflection settings. Everything else batches by what
 * an unlit material actually draws (texture, colour, vertex colours, side, blending), so materials that differ
 * only in their (unused) extras share one draw call. Normals do not count: they are dropped for those.
 */
function materialKey(mesh: Mesh, r: ReflectionSettings | null): string {
  const g = mesh.geometry;
  const attrs = Object.keys(g.attributes).filter((a) => r || a !== 'normal').sort().join('|');
  const m = mesh.material as MeshBasicMaterial;
  const look = r || !m.isMeshBasicMaterial
    ? m.uuid
    : `basic:${m.map?.uuid ?? '-'}:${m.color.getHexString()}:${m.vertexColors}:${m.side}:${m.transparent}:${m.opacity}:${m.alphaTest}:${m.toneMapped}:${m.fog}`;
  return `${look}#${attrs}#${g.index ? 'i' : 'n'}#${r ? `${r.strength},${r.roughness}` : ''}`;
}

/** The mesh's positions (and index) in world space. */
function worldPositions(mesh: Mesh): BufferGeometry {
  const src = mesh.geometry;
  const g = new BufferGeometry();
  const pos = src.attributes.position as BufferAttribute;
  g.setAttribute('position', new BufferAttribute(toFloat32(pos), 3, false));
  if (src.index) g.setIndex(new BufferAttribute(Uint32Array.from(src.index.array as ArrayLike<number>), 1));
  g.applyMatrix4(mesh.matrixWorld);
  g.computeBoundingSphere();
  return g;
}

/** Clone the geometry with all attributes as float32 (quantized meshopt attributes are int16/int8), in world space. */
function toWorldFloat(mesh: Mesh, dropNormal: boolean): BufferGeometry {
  const src = mesh.geometry;
  const g = new BufferGeometry();
  for (const [name, attr] of Object.entries(src.attributes)) {
    if (dropNormal && name === 'normal') continue;
    g.setAttribute(name, new BufferAttribute(toFloat32(attr as BufferAttribute), attr.itemSize, false));
  }
  if (src.index) g.setIndex(new BufferAttribute(Uint32Array.from(src.index.array as ArrayLike<number>), 1));
  g.applyMatrix4(mesh.matrixWorld);
  return g;
}

/** Max of a normalized integer type, and whether it is signed (three's denormalize: v / max, clamped at -1). */
const NORM = new Map<unknown, [number, boolean]>([
  [Int8Array, [127, true]], [Uint8Array, [255, false]], [Int16Array, [32767, true]], [Uint16Array, [65535, false]],
]);

/**
 * The attribute's values as a new Float32Array, as `getComponent` reads them (denormalized), but over the
 * typed array directly: `getComponent` per component was most of the batching time at load.
 */
function toFloat32(a: BufferAttribute): Float32Array {
  const n = a.count * a.itemSize;
  const out = new Float32Array(n);
  const arr = a.array as ArrayLike<number> & { constructor: unknown };
  const norm = a.normalized ? NORM.get(arr.constructor) : undefined;
  if (!(a as BufferAttribute & { isInterleavedBufferAttribute?: boolean }).isInterleavedBufferAttribute && arr.length === n && (!a.normalized || norm)) {
    if (!norm) out.set(arr);
    else if (norm[1]) for (let i = 0; i < n; i++) out[i] = Math.max(arr[i]! / norm[0], -1);
    else for (let i = 0; i < n; i++) out[i] = arr[i]! / norm[0];
    return out;
  }
  for (let i = 0, size = a.itemSize; i < a.count; i++) for (let c = 0; c < size; c++) out[i * size + c] = a.getComponent(i, c);
  return out;
}

/**
 * Drop the CPU copies of a scene's textures once all of them are on the GPU (KTX2 mip data, decoded
 * ImageBitmaps): nothing uploads them again (a lost WebGL context reloads the page). Textures that share an
 * image (same source, other sampler) are all uploaded by then.
 */
export function releaseTextureData(s: LoadedScene): void {
  for (const t of s.textures) {
    const ct = t as CompressedTexture;
    if (ct.isCompressedTexture) ct.mipmaps = [];
    const img = t.image as unknown;
    if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) img.close();
  }
}

/** Free a loaded scene's GPU resources (geometries, materials, textures). */
export function disposeScene(s: LoadedScene): void {
  const mats = new Set<Material>();
  s.root.traverse((o) => {
    const m = o as Mesh;
    if (!m.isMesh) return;
    m.geometry.dispose();
    for (const x of Array.isArray(m.material) ? m.material : [m.material]) mats.add(x);
  });
  for (const m of mats) m.dispose();
  for (const t of s.textures) t.dispose();
  for (const l of s.shapes.values()) for (const m of l) m.geometry.dispose();
  s.root.removeFromParent();
}
